import { Menu, MenuItem, MenuItemLabel, MenuItemLeading, MenuItemTrailing } from '@plato/ui/primitives/menu';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, type LucideProps } from 'lucide-react';
import type { ComponentType, FC, KeyboardEvent } from 'react';

export const menuSeparatorClassName = 'my-cozy h-px w-full bg-neutral-200';

/** Activates a MenuItem's row action from the keyboard, matching a native button. */
const onRowKeyDown = (onActivate: () => void) => (event: KeyboardEvent<HTMLLIElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onActivate();
    }
};

export type MenuBackRowProps = {
    label: string;
    onBack: () => void;
};

/**
 * Header of a pushed level: a full-width row that returns to the level above.
 * The whole row is the target rather than just the chevron — it is the only
 * exit from a pushed level, so it should be hard to miss.
 */
export const MenuBackRow: FC<MenuBackRowProps> = ({ label, onBack }) => (
    <MenuItem
        label={label}
        tabIndex={0}
        onClick={onBack}
        onKeyDown={onRowKeyDown(onBack)}
        className="cursor-pointer font-medium"
    >
        <MenuItemLeading>
            <ChevronLeftIcon />
        </MenuItemLeading>
        <MenuItemLabel />
    </MenuItem>
);

export type MenuDrillRowProps = {
    icon: ComponentType<LucideProps>;
    label: string;
    /** The current value, shown right-aligned so the row reads "label: value". */
    value: string;
    onOpen: () => void;
};

/** A setting collapsed to one row: what it is, its current value, and a way in. */
export const MenuDrillRow: FC<MenuDrillRowProps> = ({ icon: Icon, label, value, onOpen }) => (
    <MenuItem
        // The row's accessible name combines label + value ("Language
        // English"), matching what a sighted user reads from the row —
        // MenuItemLabel's own children keep the visible text split.
        label={`${label} ${value}`}
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={onRowKeyDown(onOpen)}
        className="cursor-pointer"
    >
        <MenuItemLeading>
            <Icon />
        </MenuItemLeading>
        <MenuItemLabel>
            <span className="truncate">{label}</span>
        </MenuItemLabel>
        <MenuItemTrailing>
            <span>{value}</span>
            <ChevronRightIcon />
        </MenuItemTrailing>
    </MenuItem>
);

export type MenuOptionRowProps = {
    label: string;
    selected: boolean;
    onSelect: () => void;
};

/** One choice inside a pushed level, check-marked when it is the current one. */
export const MenuOptionRow: FC<MenuOptionRowProps> = ({ label, selected, onSelect }) => (
    <MenuItem
        label={label}
        tabIndex={0}
        aria-current={selected ? 'true' : undefined}
        onClick={onSelect}
        onKeyDown={onRowKeyDown(onSelect)}
        className="cursor-pointer"
    >
        <MenuItemLabel />
        {selected && (
            <MenuItemTrailing>
                {/* A lone selection check next to plain text needs both brand color and a
                    heavier stroke to read as active (the global .lucide rule pins 1.5). */}
                <CheckIcon className="stroke-[2.5] text-brand" />
            </MenuItemTrailing>
        )}
    </MenuItem>
);

export { Menu };
